/**
 * Toolbar — 3-mode workflow (Struktur → Design → Export)
 * Ported from Builder-5 UX with Zustand store integration.
 */
import { useRef, useMemo } from 'react';
import { useBuilderStore, useTemporalStore } from '@/state/store';
import type { BuilderMode } from '@/state/store';
import { RenderIcon } from '@/components/preview/RenderIcon';
import { Toast } from '@/state/toast';

export function Toolbar() {
  const mode = useBuilderStore((s) => s.mode);
  const viewport = useBuilderStore((s) => s.viewport);
  const showGrid = useBuilderStore((s) => s.showGrid);
  const show8pxRaster = useBuilderStore((s) => s.show8pxRaster);
  const builderTheme = useBuilderStore((s) => s.builderTheme);
  const setMode = useBuilderStore((s) => s.setMode);
  const setViewport = useBuilderStore((s) => s.setViewport);
  const setOpenModal = useBuilderStore((s) => s.setOpenModal);
  const toggleGrid = useBuilderStore((s) => s.toggleGrid);
  const toggle8pxRaster = useBuilderStore((s) => s.toggle8pxRaster);
  const toggleBuilderTheme = useBuilderStore((s) => s.toggleBuilderTheme);
  const importData = useBuilderStore((s) => s.importData);

  const undo = useTemporalStore((s) => s.undo);
  const redo = useTemporalStore((s) => s.redo);
  const pastStates = useTemporalStore((s) => s.pastStates);
  const futureStates = useTemporalStore((s) => s.futureStates);

  const canUndo = pastStates.length > 0;
  const canRedo = futureStates.length > 0;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const modeTabs = useMemo(() => [
    { id: 'structure' as BuilderMode, label: 'Struktur', step: 1 },
    { id: 'design' as BuilderMode, label: 'Design', step: 2 },
    { id: 'export' as BuilderMode, label: 'Export', step: 3 },
  ], []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target?.result as string);
          importData(data);
          Toast.success('Projekt importiert');
        } catch {
          Toast.error('Ungültige JSON-Datei');
        }
      };
      reader.readAsText(file);
    }
    e.target.value = '';
  };

  return (
    <header className="toolbar" role="banner">
      <div className="toolbar__left">
        <div className="toolbar__brand">
          <span className="toolbar__brandMark">SB</span>
          <span className="toolbar__brandText">Studio Builder</span>
        </div>
        <nav className="toolbar__tabs">
          {modeTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`toolbar__tab ${mode === tab.id ? 'is-active' : ''}`}
              onClick={() => setMode(tab.id)}
            >
              <span className="toolbar__tabStep">{tab.step}</span>
              <span className="toolbar__tabLabel">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      <div className="toolbar__right">
        {/* Undo / Redo */}
        <div className="toolbar__group">
          <button
            type="button"
            className="toolbar__btn toolbar__btn--icon"
            onClick={() => undo()}
            disabled={!canUndo}
            title="Rückgängig (Ctrl+Z)"
          >
            <RenderIcon name="undo" />
          </button>
          <button
            type="button"
            className="toolbar__btn toolbar__btn--icon"
            onClick={() => redo()}
            disabled={!canRedo}
            title="Wiederholen (Ctrl+Y)"
          >
            <RenderIcon name="redo" />
          </button>
        </div>

        {/* Grid / 8px — only in Structure mode */}
        {mode === 'structure' && (
          <div className="toolbar__group">
            <button
              type="button"
              className={`toolbar__btn ${showGrid ? 'is-active' : ''}`}
              onClick={toggleGrid}
            >
              <span className="toolbar__btnLabel">Grid</span>
            </button>
            <button
              type="button"
              className={`toolbar__btn ${show8pxRaster ? 'is-active' : ''}`}
              onClick={toggle8pxRaster}
            >
              <span className="toolbar__btnLabel">8px</span>
            </button>
          </div>
        )}

        {/* Viewport — always visible */}
        <div className="toolbar__group">
          <button
            type="button"
            className={`toolbar__btn toolbar__btn--icon ${viewport === 'desktop' ? 'is-active' : ''}`}
            onClick={() => setViewport('desktop')}
            title="Desktop"
          >
            <RenderIcon name="monitor" />
          </button>
          <button
            type="button"
            className={`toolbar__btn toolbar__btn--icon ${viewport === 'tablet' ? 'is-active' : ''}`}
            onClick={() => setViewport('tablet')}
            title="Tablet"
          >
            <RenderIcon name="tablet" />
          </button>
          <button
            type="button"
            className={`toolbar__btn toolbar__btn--icon ${viewport === 'mobile' ? 'is-active' : ''}`}
            onClick={() => setViewport('mobile')}
            title="Mobile"
          >
            <RenderIcon name="smartphone" />
          </button>
        </div>

        <div className="toolbar__divider" />

        {/* Import (hidden file input) */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button
          type="button"
          className="toolbar__btn"
          onClick={() => fileInputRef.current?.click()}
          title="Projekt importieren"
        >
          <RenderIcon name="upload" />
          <span className="toolbar__btnLabel">Import</span>
        </button>

        {/* Export */}
        {mode === 'export' && (
          <button
            type="button"
            className="toolbar__btn toolbar__btn--primary"
            onClick={() => setOpenModal('export')}
          >
            <RenderIcon name="download" />
            <span className="toolbar__btnLabel">Export</span>
          </button>
        )}

        {/* Theme toggle */}
        <button
          type="button"
          className="toolbar__btn toolbar__btn--icon"
          onClick={toggleBuilderTheme}
          title={builderTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
        >
          <RenderIcon name={builderTheme === 'light' ? 'moon' : 'sun'} />
        </button>
      </div>
    </header>
  );
}
